import React, { useState, useEffect } from 'react';
import { productivityApi, projectApi } from '../services/api';
import { Card, Select, Spinner, Badge } from '../components/UI';
import { ChartBar, Users, Package, TrendUp } from '@phosphor-icons/react';

export default function ProductivityPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [teamWork, setTeamWork] = useState([]);
  const [manpower, setManpower] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectApi.list().then(r => setProjects(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const load = async () => {
      const pid = selectedProject || undefined;
      const [tw, mp, mat] = await Promise.all([
        productivityApi.teamWork(pid),
        productivityApi.manpowerOutput(pid),
        productivityApi.materialUsage(pid)
      ]);
      setTeamWork(tw.data);
      setManpower(mp.data);
      setMaterials(mat.data);
    };
    load();
  }, [selectedProject]);

  // Group materials by team
  const materialsByTeam = {};
  materials.forEach(m => {
    if (!materialsByTeam[m.team_name]) materialsByTeam[m.team_name] = [];
    materialsByTeam[m.team_name].push(m);
  });

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5" data-testid="productivity-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Team Productivity</h1>
          <p className="text-sm text-[#666666]">Work done, manpower allocation, and material usage</p>
        </div>
        <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="w-64" data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      {/* ===== TEAM WORK DONE ===== */}
      <Card className="p-4" data-testid="team-work-section">
        <div className="flex items-center gap-2 mb-3">
          <ChartBar className="w-5 h-5 text-[#002FA7]" weight="fill" />
          <h2 className="font-semibold text-[#0A0A0A]">Team-wise Work Done</h2>
        </div>
        {teamWork.length === 0 ? (
          <p className="text-sm text-[#999] text-center py-4">No data yet</p>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm" data-testid="team-work-table">
              <thead>
                <tr className="text-left text-[10px] text-[#999] uppercase tracking-wider border-b border-[#E0E0E0]">
                  <th className="pb-2 pl-4 sm:pl-0">Team</th>
                  <th className="pb-2 text-right">Logs</th>
                  <th className="pb-2 text-right">Days</th>
                  <th className="pb-2 text-right">Avg Progress</th>
                  <th className="pb-2 text-right pr-4 sm:pr-0">Trend</th>
                </tr>
              </thead>
              <tbody>
                {teamWork.map(t => (
                  <tr key={t.team_id} className="border-b border-[#F0F0F0] last:border-0" data-testid={`work-row-${t.team_id}`}>
                    <td className="py-2.5 pl-4 sm:pl-0 font-medium text-[#0A0A0A]">{t.team_name}</td>
                    <td className="py-2.5 text-right text-[#666]">{t.total_logs}</td>
                    <td className="py-2.5 text-right text-[#666]">{t.days_worked}</td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <div className="w-16 h-1.5 bg-[#E0E0E0] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${t.avg_progress}%`, backgroundColor: t.avg_progress >= 80 ? '#00C853' : t.avg_progress >= 50 ? '#FFAB00' : '#002FA7' }} />
                        </div>
                        <span className="text-xs font-mono">{t.avg_progress}%</span>
                      </div>
                    </td>
                    <td className="py-2.5 text-right pr-4 sm:pr-0">
                      <TrendUp className="w-4 h-4 text-[#00C853] inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ===== MANPOWER VS OUTPUT ===== */}
      <Card className="p-4" data-testid="manpower-section">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-5 h-5 text-[#FFAB00]" weight="fill" />
          <h2 className="font-semibold text-[#0A0A0A]">Manpower vs Output</h2>
        </div>
        {manpower.length === 0 ? (
          <p className="text-sm text-[#999] text-center py-4">No data yet</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {manpower.map(m => (
              <div key={m.team_id} className="bg-[#F9F9F9] rounded-lg p-3" data-testid={`manpower-card-${m.team_id}`}>
                <p className="font-semibold text-sm text-[#0A0A0A] mb-2">{m.team_name}</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-lg font-bold text-[#0A0A0A]">{m.total_workers}</p>
                    <p className="text-[10px] text-[#999] uppercase">Workers</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#0A0A0A]">{m.total_hours}</p>
                    <p className="text-[10px] text-[#999] uppercase">Hours</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#002FA7]">{m.total_man_hours}</p>
                    <p className="text-[10px] text-[#999] uppercase">Man-Hrs</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {m.categories.map(c => (
                    <Badge key={c} variant="default">{c}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ===== MATERIAL USAGE ===== */}
      <Card className="p-4" data-testid="material-section">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-5 h-5 text-[#00C853]" weight="fill" />
          <h2 className="font-semibold text-[#0A0A0A]">Material Usage by Team</h2>
        </div>
        {materials.length === 0 ? (
          <p className="text-sm text-[#999] text-center py-4">No data yet</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(materialsByTeam).map(([teamName, mats]) => (
              <div key={teamName} data-testid={`material-team-${teamName}`}>
                <p className="text-xs font-semibold text-[#666] uppercase tracking-wide mb-1.5">{teamName}</p>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                        <th className="pb-1.5 pl-4 sm:pl-0">Material</th>
                        <th className="pb-1.5 text-right">Received</th>
                        <th className="pb-1.5 text-right">Used</th>
                        <th className="pb-1.5 text-right pr-4 sm:pr-0">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mats.map((m, i) => (
                        <tr key={i} className="border-b border-[#F0F0F0] last:border-0">
                          <td className="py-1.5 pl-4 sm:pl-0 font-medium text-[#0A0A0A]">{m.material_name}</td>
                          <td className="py-1.5 text-right text-[#666]">{m.total_received}</td>
                          <td className="py-1.5 text-right text-[#666]">{m.total_used}</td>
                          <td className={`py-1.5 text-right pr-4 sm:pr-0 font-bold ${m.balance < 0 ? 'text-[#FF3333]' : 'text-[#00C853]'}`}>
                            {m.balance}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
